import apiClient from './apiClient';
import { API_URL, ENDPOINTS } from '../config';

// Define the search result interface
export interface GlobalSearchResult {
  id: string;
  title: string;
  subtitle?: string;
  route: string;
  category: string;
  icon?: string;
  searchParam: string;
  searchField?: string; // The field to search in on the destination page
}

// Define the search options interface
export interface GlobalSearchOptions {
  includeCustomers?: boolean;
  includeInvoices?: boolean;
  includeInventory?: boolean;
  includeQuotes?: boolean;
  includeSuppliers?: boolean;
  includeUsers?: boolean;
  includePurchaseOrders?: boolean;
  limit?: number;
}

// Define backend response interfaces
interface BackendSearchData {
  id: string;
  type: string;
  // Common fields
  name?: string;
  po_number?: string;
  // User fields
  user_name?: string;
  email?: string;
  mobile_number?: string;
  role?: string;
  // Invoice fields
  invoice_number?: string;
  customer_name?: string;
  date?: string;
  total_amount?: number;
  status?: string;
  // Supplier fields
  phone?: string;
  contact_person?: string;
  // Purchase Order fields
  supplier_name?: string;
  issue_date?: string;
  // Customer fields
  phone_no?: string;
  company_name?: string;
  // Inventory fields
  sku_code?: string;
  style_code?: string;
  carton?: string;
  units_per_carton?: number;
  carton_dimensions?: string;
  // Supplier SKU fields
  description?: string;
  supplier_id?: string;
}

interface BackendSearchModule {
  module: string;
  data: BackendSearchData[];
}

interface BackendSearchResponse {
  query: string;
  page: number;
  limit: number;
  results: BackendSearchModule[];
}

/**
 * Convert backend search results to frontend format
 */
const convertBackendResults = (backendResults: BackendSearchModule[]): GlobalSearchResult[] => {
  const frontendResults: GlobalSearchResult[] = [];

  backendResults.forEach(module => {
    module.data.forEach(item => {
      let result: GlobalSearchResult;

      switch (item.type) {
        case 'user':
          result = {
            id: `user-${item.id}`,
            title: item.user_name || item.email || 'Unknown User',
            subtitle: `${item.email || ''} | ${item.role || ''}`.trim().replace(/^\||\|$/g, ''),
            route: '/users',
            category: 'Users',
            searchParam: item.user_name || item.email || '',
          };
          break;

        case 'customer':
          result = {
            id: `customer-${item.id}`,
            title: item.company_name || item.name || 'Unknown Customer',
            subtitle: `${item.email || ''} | ${item.phone || ''}`.trim().replace(/^\||\|$/g, ''),
            route: '/customers',
            category: 'Customers',
            searchParam: item.company_name || item.name || '',
          };
          break;

        case 'invoice':
          result = {
            id: `invoice-${item.id}`,
            title: item.invoice_number || 'Unknown Invoice',
            subtitle: `${item.customer_name || ''} | $${item.total_amount?.toFixed(2) || '0.00'}`.trim().replace(/^\||\|$/g, ''),
            route: '/invoices',
            category: 'Invoices',
            searchParam: item.invoice_number || item.customer_name || '',
            searchField: item.invoice_number ? 'invoiceNo' : 'customerName'
          };
          break;

        case 'quote':
          result = {
            id: `quote-${item.id}`,
            title: item.name || (item as any).quote_no || (item as any).quote_number || 'Unknown Quote',
            subtitle: `${item.customer_name || ''} | ${item.status || ''}`.trim().replace(/^\||\|$/g, ''),
            route: '/quotes',
            category: 'Quotes',
            searchParam: (item as any).quote_no || (item as any).quote_number || item.customer_name || ''
          };
          break;

        case 'supplier':
          result = {
            id: `supplier-${item.id}`,
            title: item.name || 'Unknown Supplier',
            subtitle: `${item.email || ''} | ${item.phone || ''}`.trim().replace(/^\||\|$/g, ''),
            route: '/suppliers',
            category: 'Suppliers',
            searchParam: item.name || '',
          };
          break;

        case 'purchase_order':
          result = {
            id: `purchase_order-${item.id}`,
            title: item.po_number || `PO-${item.id}`,
            subtitle: `${item.supplier_name || ''} | ${item.status || ''}`.trim().replace(/^\||\|$/g, ''),
            route: '/po-management',
            category: 'Purchase Orders',
            searchParam: (item.po_number as any) || String(item.id),
          };
          break;

        case 'inventory_item':
          result = {
            id: `inventory-${item.id}`,
            title: item.sku_code || 'Unknown SKU',
            subtitle: `${item.style_code || ''} | ${item.supplier_name || ''}`.trim().replace(/^\||\|$/g, ''),
            route: '/inventory',
            category: 'Inventory',
            searchParam: item.sku_code || '',
            searchField: 'sku_code'
          };
          break;

        case 'supplier_sku':
          // Ensure we have a valid SKU code for the title
          let skuCode = (item.sku_code || '').trim();
          if (!skuCode) {
            return; // Skip items without SKU codes
          }
          
          // Validate SKU code - filter out invalid ones that look like descriptions
          const invalidPatterns = [
            'button:', 'package', 'size:', 'finish', 'overflow', 'adjustable',
            'plug & waste', 'included', 'not included', 'bathtub', 'bath tub',
            'freestanding', 'corner'
          ];
          
          // Skip if SKU code is invalid (too long, contains description patterns, or has too many special chars)
          if (skuCode.length > 100 || 
              invalidPatterns.some(pattern => skuCode.toLowerCase().includes(pattern.toLowerCase())) ||
              (skuCode.match(/"/g) || []).length > 2 ||  // Too many quotes
              (skuCode.match(/,/g) || []).length > 3) {  // Too many commas
            return; // Skip invalid SKU codes
          }
          
          // Format subtitle similar to inventory items for consistency
          // Use style_code first (if available), then supplier name (like inventory format)
          const styleCode = (item.style_code || '').trim();
          const supplierName = (item.supplier_name || '').trim();
          
          // Build subtitle: style_code | supplier (matching inventory format)
          // Note: We don't include description in subtitle to keep it clean and consistent
          const subtitleParts = [];
          if (styleCode) subtitleParts.push(styleCode);
          if (supplierName) subtitleParts.push(supplierName);
          
          result = {
            id: `supplier-sku-${item.id}`,
            title: skuCode, // Always use SKU code as title
            subtitle: subtitleParts.join(' | ') || '', // style_code | supplier
            route: '/suppliers-dashboard',
            category: 'Supplier SKUs',
            searchParam: skuCode,
            searchField: 'sku_code'
          };
          break;

        default:
          return; // Skip unknown types
      }

      if (result.title && result.title.trim() !== '') {
        frontendResults.push(result);
      }
    });
  });

  return frontendResults;
};

/**
 * Global search using backend API
 */
export const globalSearch = async (
  query: string,
  options: GlobalSearchOptions = {}
): Promise<GlobalSearchResult[]> => {
  if (!query || query.trim() === '' || query.trim().length < 2) {
    return [];
  }

  const {
    limit = 5
  } = options;

  try {
    // Make API call to backend search endpoint
    const response = await apiClient.get<BackendSearchResponse>(
      `${ENDPOINTS.GLOBAL_SEARCH}?query=${encodeURIComponent(query.trim())}&limit=${limit}`
    );

    if (!response || !response.results) {
      console.warn('Invalid search response format from backend');
      return [];
    }

    // Convert backend results to frontend format
    let frontendResults = convertBackendResults(response.results);

    // For quote-like queries, also pull from the quotes reports endpoint so we can
    // show individual quote rows similar to the invoices dropdown (even if the
    // unified backend search returns nothing for quotes)
    const looksLikeQuote = /^\s*quo[-\s]?/i.test(query);
    if (looksLikeQuote) {
      let gotAny = false;
      // Primary: reports endpoint (preferred for search)
      try {
        const params = new URLSearchParams();
        params.append('search', query.trim());
        params.append('limit', String(options.limit || 5));
        const quotesResp: any = await apiClient.get(`${ENDPOINTS.QUOTES_REPORTS}?${params.toString()}`);
        if (quotesResp && Array.isArray(quotesResp.items)) {
          const quoteResults: GlobalSearchResult[] = quotesResp.items.slice(0, options.limit || 5).map((q: any, idx: number) => {
            const quoteNo = q.quote_no || q.quote_number || q.quoteNo || q.quote_id || '';
            const company = q.customer_name || q.company_name || (q.company && q.company.name) || '';
            const status = q.status || '';
            return {
              id: `quote-fallback-${idx}-${quoteNo}`,
              title: quoteNo || 'Unknown Quote',
              subtitle: `${company}${status ? ' | ' + status : ''}`.trim(),
              route: '/quotes',
              category: 'Quotes',
              searchParam: quoteNo || query.trim(),
            } as GlobalSearchResult;
          });
          const seen = new Set<string>();
          const merged = [...quoteResults, ...frontendResults].filter(r => {
            const key = `${r.category}:${r.title}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          frontendResults = merged;
          gotAny = quoteResults.length > 0;
        }
      } catch (_) {
        // ignore and try fallback
      }

      // Fallback: quotes listing endpoint with search support (some environments expose this)
      if (!gotAny) {
        try {
          const params2 = new URLSearchParams();
          params2.append('page', '1');
          params2.append('limit', String(options.limit || 5));
          params2.append('search', query.trim());
          const listResp: any = await apiClient.get(`${ENDPOINTS.QUOTES}?${params2.toString()}`);
          const items = Array.isArray(listResp?.items) ? listResp.items : Array.isArray(listResp) ? listResp : [];
          if (items.length > 0) {
            const quoteResults2: GlobalSearchResult[] = items.slice(0, options.limit || 5).map((q: any, idx: number) => {
              const quoteNo = q.quote_no || q.quote_number || q.quoteNo || q.quote_id || '';
              const company = q.customer_name || q.company_name || (q.company && q.company.name) || '';
              const status = q.status || '';
              return {
                id: `quote-fallback2-${idx}-${quoteNo}`,
                title: quoteNo || 'Unknown Quote',
                subtitle: `${company}${status ? ' | ' + status : ''}`.trim(),
                route: '/quotes',
                category: 'Quotes',
                searchParam: quoteNo || query.trim(),
              } as GlobalSearchResult;
            });
            const seen2 = new Set<string>();
            const merged2 = [...quoteResults2, ...frontendResults].filter(r => {
              const key = `${r.category}:${r.title}`;
              if (seen2.has(key)) return false;
              seen2.add(key);
              return true;
            });
            frontendResults = merged2;
          }
        } catch (_) {
          // final fallback ignored
        }
      }
    }

    console.log(`Backend search completed: ${frontendResults.length} results for "${query}"`);
    return frontendResults;

  } catch (error) {
    console.error('Error performing backend global search:', error);
    
    // Return empty results on error instead of throwing
    return [];
  }
};

// Individual search functions are now deprecated since we use backend global search
// But keeping them for backward compatibility in case they're used elsewhere

/**
 * @deprecated Use globalSearch instead - this now calls the backend
 */
const searchCustomers = async (query: string, limit: number = 5): Promise<GlobalSearchResult[]> => {
  const results = await globalSearch(query, { limit, includeCustomers: true });
  return results.filter(r => r.category === 'Customers');
};

/**
 * @deprecated Use globalSearch instead - this now calls the backend
 */
const searchInvoices = async (query: string, limit: number = 5): Promise<GlobalSearchResult[]> => {
  const results = await globalSearch(query, { limit, includeInvoices: true });
  return results.filter(r => r.category === 'Invoices');
};

/**
 * @deprecated Use globalSearch instead - this now calls the backend
 */
const searchInventory = async (query: string, limit: number = 5): Promise<GlobalSearchResult[]> => {
  const results = await globalSearch(query, { limit, includeInventory: true });
  return results.filter(r => r.category === 'Inventory');
};

/**
 * @deprecated Use globalSearch instead - this now calls the backend
 */
const searchQuotes = async (query: string, limit: number = 5): Promise<GlobalSearchResult[]> => {
  const results = await globalSearch(query, { limit, includeQuotes: true });
  return results.filter(r => r.category === 'Quotes');
};

/**
 * @deprecated Use globalSearch instead - this now calls the backend
 */
const searchSuppliers = async (query: string, limit: number = 5): Promise<GlobalSearchResult[]> => {
  const results = await globalSearch(query, { limit, includeSuppliers: true });
  return results.filter(r => r.category === 'Suppliers');
};

// Export service with all functions
const globalSearchService = {
  searchInvoices,
  searchCustomers,
  searchQuotes,
  searchInventory,
  searchSuppliers,
  globalSearch
};

export default globalSearchService;
